<template>
  <div class="new-ponderation-wrapper">
    <div class="ponderation-columns-wrapper">
      <div v-for="column in ponderationColumns" :key="column.id" class="ponderation-column-container">
        <div v-tooltip="column.tooltip" class="column-title">
          <div>{{ $t(column.title) }}</div>
          <div v-if="column?.coeff" class="coeff">{{ column.coeff }}</div>
        </div>
        <Draggable :disabled="column.id === 'ignored' && disableIgnored" v-tooltip="column.id === 'ignored' && disableIgnored ? 'Les compétences si dessous ont été ignorées lors de la création de la campagne et ne peuvent donc pas être déplacé dans les autres colonnes' : null" class="column-skill-list" v-model="column.items" group="tasks" item-key="id">
          <template #item="{ element }">
            <div v-tooltip="isDisabledInEssentials(element) ? $t('app.campaign.newPage.notAvailableEssential') : null" class="skill" :class="{style: skills.find(s => (s.id === element || s.id_v1 === element || s.id_v2 === element) && s.type === 'style'), 'disabled-in-essential': isDisabledInEssentials(element)}">{{ skillLocale(element) }}</div>
          </template>
        </Draggable>
      </div>
    </div>
    <div v-if="isCampaignV3OrMore(props?.modelValue)" class="style-toggle-wrapper">
      <h4>{{ $t('app.campaign.ponderation.evaluateStyles') }}</h4>
      <div class="styles">
        <div class="toggle-container">
          {{ $t('skills.motivation.name') }}
          <div class="toggle" @click="toggleStyle('motivation')">
            <input type="checkbox" :disabled="props?.disableIgnored" :checked="isMotivationAssessed" />
            <label />
          </div>
        </div>
        <div class="toggle-container">
          {{ $t('skills.leadership.name') }}
          <div class="toggle" @click="toggleStyle('leadership')">
            <input type="checkbox" :disabled="props?.disableIgnored" :checked="isLeadershipAssessed" />
            <label />
          </div>
        </div>
        <div class="toggle-container">
          {{ $t('skills.learning_strategy.name') }}
          <div class="toggle" @click="toggleStyle('learning_strategy')">
            <input type="checkbox" :disabled="props?.disableIgnored" :checked="isLearningStrategyAssessed" />
            <label />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Campaign, CampaignCreation, ModuleLabel, WeightingModule } from '@/types/campaign';
import { useSkillsStore } from '@/stores/skills';
import Draggable from 'vuedraggable';

const { skills, styleSkills, isSkillCompatible, isCampaignV2, isCampaignV1, isCampaignV3OrMore } = useSkillsStore();
const { te, t } = useI18n();

const ponderationColumns = ref([]);

const props = defineProps<{
  modelValue: CampaignCreation,
  disableIgnored?: boolean,
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: CampaignCreation): void
}>();

function skillLocale(internal_name: string) {
  if (te(`skills.${internal_name}.name`)) {
    return t(`skills.${internal_name}.name`);
  }
  const skillInfo = skills.find(v => v.id === internal_name);
  if (te(`skills.${skillInfo?.id}.name`)) {
  } else if (te(`skills.${skillInfo?.id_v2}.name`)) {
    return t(`skills.${skillInfo?.id_v2}.name`);
  } else {
    return t(`skills.${skillInfo?.id_v1}.name`);
  }
}

function isDisabledInEssentials(id: string) {
  const skill = skills.find(s => s.id === id || s.id_v2 === id || s.id_v1 === id);
  const isDisabled = skill?.isAvailaibleInEssential === false && isCampaignV2(props.modelValue) && props.modelValue.variant === 'essentials';
  return isDisabled;
}

function toggleStyle(style: ModuleLabel) {
  if (props.modelValue.assessment.modules.includes(style)) {
    props.modelValue.assessment.modules = props.modelValue.assessment.modules.filter((m) => m !== style);
  } else {
    props.modelValue.assessment.modules.push(style);
  }
}

const isLeadershipAssessed = computed(() => {
  if (isCampaignV3OrMore(props.modelValue)) {
    return props?.modelValue?.assessment?.modules?.includes('leadership') ?? false;
  } else {
    return isSkillCompatible(props.modelValue.campaign_version, 'leadership');
  }
});

const isMotivationAssessed = computed(() => {
  if (isCampaignV3OrMore(props.modelValue)) {
    return props?.modelValue?.assessment?.modules?.includes('motivation') ?? false;
  } else {
    return isSkillCompatible(props.modelValue.campaign_version, 'motivation');
  }
});

const isLearningStrategyAssessed = computed(() => {
  if (isCampaignV3OrMore(props.modelValue)) {
    return props?.modelValue?.assessment?.modules?.includes('learning_strategy') ?? false;
  } else {
    return isSkillCompatible(props.modelValue.campaign_version, 'learning_strategy');
  }
});

const requiredSkills = computed(() => props?.modelValue?.assessment?.weighting === 'neutral' ? skills.reduce((acc, s) => {
  if (!s.disabled && !s.incompatible_versions.includes(props.modelValue.campaign_version)) acc.push(s.id);
  return acc;
}, []) : ([...props?.modelValue?.assessment?.weighting?.required as string[]]));

const primarySkills = computed(() => {
  if (props?.modelValue?.assessment?.weighting === 'neutral') return [];
  let primary = [...props?.modelValue?.assessment?.weighting?.primary];
  return primary;
});

const secondarySkills = computed(() => {
  if (props?.modelValue?.assessment?.weighting === 'neutral') return [];
  return [...props?.modelValue?.assessment?.weighting?.secondary];
});

const ignoredSkills = computed(() => {
  if (props?.modelValue?.assessment?.weighting === 'neutral') return [];
  let ignored = [...props?.modelValue?.assessment?.weighting?.ignored];
  if (!isSkillCompatible(props.modelValue.campaign_version, 'self_efficacy')) {
    ignored = ignored.filter((v) => v !== 'self_efficacy');
  }
  return ignored;
});

watch(ponderationColumns, () => {
  if (isCampaignV3OrMore(props.modelValue)) {
    if (typeof props.modelValue.assessment.weighting !== 'string') {
      props.modelValue.assessment.weighting.required = ponderationColumns.value[0].items;
      props.modelValue.assessment.weighting.primary = ponderationColumns.value[1].items;
      props.modelValue.assessment.weighting.secondary = ponderationColumns.value[2].items;
      props.modelValue.assessment.weighting.ignored = ponderationColumns.value[3].items;
    }

    const weighting = ponderationColumns.value.flatMap((column) => column.items);
    weighting.forEach((skillId) => {
      if (!props.modelValue.assessment.modules.includes(skillId as WeightingModule)) {
        props.modelValue.assessment.modules.push(skillId as WeightingModule);
      }
    });
    console.log(props.modelValue.assessment.modules);
    const finalIgnoredStyles = ponderationColumns.value[3].items.filter((item => skills.map(s => s.id).includes(item)));
    props.modelValue.assessment.modules = props.modelValue.assessment.modules.filter((m) => !finalIgnoredStyles.includes(m as WeightingModule));
  } else {
    if (typeof props.modelValue.assessment.weighting === 'string') {
      props.modelValue.assessment.weighting = {
        required: [],
        primary: [],
        secondary: [],
        ignored: [],
      };
    }
    props.modelValue.assessment.weighting.required = ponderationColumns.value[0].items;
    props.modelValue.assessment.weighting.primary = ponderationColumns.value[1].items;
    props.modelValue.assessment.weighting.secondary = ponderationColumns.value[2].items;
    props.modelValue.assessment.weighting.ignored = ponderationColumns.value[3].items;
  }
}, {deep: true});

onBeforeMount(() => {
  ponderationColumns.value = [
    { id: "required", title: "app.campaign.ponderation.mandatory", tooltip: "Coefficient : 3", items: requiredSkills.value, coeff: "coeff. 3" },
    { id: "primary", title: "app.campaign.ponderation.important", tooltip: "Coefficient : 2", items: primarySkills.value, coeff: "coeff. 2" },
    { id: "secondary", title: "app.campaign.ponderation.secondary", tooltip: "Coefficient : 1", items: secondarySkills.value, coeff: "coeff. 1" },
    { id: "ignored", title: "app.campaign.ponderation.excluded", items: ignoredSkills.value },
  ];
});

</script>

<style scoped lang="scss">
  @import "@/assets/style/variables.scss";

  .style-toggle-wrapper {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .styles {
      display: flex;
      gap: 24px;
      align-items: center;
    }
  }

  .styles-wrapper {
    display: flex;
    gap: 12px;

    .assessed-or-ignored-styles {
      display: flex;
      gap: 6px;
      align-items: center;
      justify-content: space-between;

      .title {
        padding: 4px 12px;
        background-color: #9E64E9;
        border-radius: 4px;
        color: $black-color;
        text-align: center;
        font-size: 13px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
        text-transform: uppercase;
      }

      .style-list {
        display: flex;
        align-items: center;
        gap: 6px;

        .item {
          display: flex;
          font-weight: 600;
          
          &::after {
            content: ", ";
          }
  
          &:last-child {
            &::after {
              display: none;
            }
          }
        }
      }
    }
  }

  .new-ponderation-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .ponderation-columns-wrapper {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-shrink: 1;
    flex-grow: 1;
    height: 90%;
    width: 100%;
  }

  .ponderation-column-container {
    display: flex;
    padding: 16px;
    flex-direction: column;
    gap: 24px;
    flex: 1 0 0;
    min-height: 300px;
    align-self: stretch;

    border-radius: 12px;
    border: 1px solid var(--light-grey-color);
    background-color: var(--light-white-color);

    .column-title {
      display: flex;
      align-items: center;
      justify-content: space-between;

      color: var(--secondary-color);
      //text-align: center;
      font-family: $bioSans;
      font-size: 13px;
      font-weight: 600;

      .coeff {
        padding: 3px 6px;
        background-color: $primary-color;
        border-radius: 4px;
        color: $secondary-color;

        font-size: 10px;
        font-weight: 600;
      }
    }

    &:last-child {
      .column-skill-list {
        .skill {
          background: $disabled-background;
          color: $disabled-color;
          border-color: $disabled-color !important;
        }
      }
    }
    
    .column-skill-list {
      display: flex;
      align-items: flex-start;
      align-content: flex-start;
      gap: 8px;
      align-self: stretch;
      flex-wrap: wrap;
      overflow-y: auto;
      flex-basis: 100%;

      &::-webkit-scrollbar {
        width: 3px;
        border-radius: 24px;
        background-color: var(--light-grey-color);
      }

      &::-webkit-scrollbar-thumb {
        background: $primary-color;
        padding: 4px;
        width: 3px;
        border-radius: 24px;
      }

      .skill {
        padding: 6px 8px;

        border-radius: 4px;
        border: 1px solid var(--primary-color);
        background: var(--primary-100-color);

        color: vaar(--secondary-color);
        font-family: $bioSans;
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        text-align: center;

        cursor: grab;
        user-select: none;

        /* While dragging */
        &.sortable-chosen {
          cursor: grabbing !important;
        }

        /* Item being dragged */
        &.sortable-drag {
          cursor: grabbing !important;
        }

        &.style {
          border: 2px solid #9E64E9;
        }

        &.disabled-in-essential {
          background: $disabled-background;
          color: $disabled-color;
          border-color: $disabled-color !important;
        }
      }
    }
  }
</style>